import { Logo } from '../brand/Logo.js';
import { Button } from '../components/Button.js';
import { cn } from '../lib/cn.js';

export interface LockScreenProps {
  /** Why the workbench is locked, one or two sentences. */
  message: string;
  /** Mono value: how long local read access remains, e.g. `41m` or `--`. */
  remaining?: string;
  remainingLabel?: string;
  onSignIn: () => void;
  signInLabel?: string;
  /** Secondary action (quit, work offline read-only). */
  secondary?: { label: string; onSelect: () => void };
  /** Mono footer line (user, device, version). */
  footer?: string;
  loading?: boolean;
  className?: string;
}

/** Full-window lock (spec section 2): lockup, message, remaining window, sign-in action. */
export function LockScreen({ message, remaining, remainingLabel = 'Remaining window', onSignIn, signInLabel = 'Sign in', secondary, footer, loading = false, className }: LockScreenProps) {
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="lock-title" className={cn('flex h-screen w-screen flex-col items-center justify-center bg-bg px-6 text-ink', className)}>
      <div className="w-full max-w-[400px] rounded-sm border border-line bg-panel">
        <div className="flex h-14 items-center border-b border-line px-5">
          <Logo height={18} />
        </div>
        <div className="px-5 py-5">
          <p className="label-mono">Locked</p>
          <h1 id="lock-title" className="mt-2 text-2xl font-medium tracking-tight">
            Sign in to continue
          </h1>
          <p className="mt-2 text-body leading-relaxed text-ink/85">{message}</p>
          <div className="mt-5 rounded-sm border border-line bg-bg px-3 py-2">
            <p className="label-mono">{remainingLabel}</p>
            <p className="mt-1 font-mono text-xl tabular-nums leading-none text-ink">{remaining ?? '--'}</p>
          </div>
          <div className="mt-5 flex items-center justify-between gap-3">
            {secondary ? (
              <Button variant="ghost" trailingArrow={false} onClick={secondary.onSelect}>
                {secondary.label}
              </Button>
            ) : (
              <span />
            )}
            <Button variant="primary" onClick={onSignIn} loading={loading}>
              {signInLabel}
            </Button>
          </div>
        </div>
        {footer ? <div className="border-t border-hair px-5 py-2.5 font-mono text-2xs text-mute">{footer}</div> : null}
      </div>
    </div>
  );
}
